import React from "react";

import {
  colToLetter,
  parseCellRef,
  SpreadsheetCellData,
  SpreadsheetSheetLayout,
  useSpreadsheetMeta,
  useSpreadsheetSheet,
} from "@/api/spreadsheet";
import { useDocumentAnswers } from "@/api/q-and-a";
import LoadingSpinner from "../../loading-spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { NUMERIC_CELL_CLASS } from "@/components/ui/data-table-helpers";
import { cn } from "@/lib/utils";

const GUTTER_WIDTH = 48;
const HEADER_HEIGHT = 24;
const OVERSCAN_ROWS = 10;
// Excel defaults: 8.43-character columns (~64px), 15pt rows (20px).
const DEFAULT_COL_CHARS = 8.43;
const DEFAULT_ROW_PT = 15;
// Render-cap for very wide sheets; row windowing keeps tall sheets cheap.
const MAX_RENDER_COLS = 200;
// How many empty neighbor columns a text cell may spill across (Excel-style).
const MAX_SPILL_COLS = 10;

// A merge this large is pathological (e.g. a whole column merged); render it
// unmerged rather than materializing tens of thousands of covered cells.
const MAX_MERGE_AREA = 5000;

const colCharsToPx = (chars: number) => Math.round(chars * 7 + 5);
const ptToPx = (pt: number) => Math.round((pt * 4) / 3);

const range = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);

interface SpreadsheetViewerProps {
  documentId: string;
  /** Same target the PDF viewer receives; citationId enables cell deep-link. */
  target?: {
    instanceId: string;
    groupId?: string;
    citationId?: string;
  };
}

interface CitedCell {
  sheet: string;
  row: number | null;
  col: number | null;
  imprecise: boolean;
}

/** Resolve the cited spreadsheet cell from the document's answers, if any. */
const useCitedCell = (
  documentId: string,
  citationId?: string,
): CitedCell | null => {
  const { data: answeredQuestions } = useDocumentAnswers({
    documentId,
    enabled: !!citationId,
  });

  return React.useMemo(() => {
    if (!citationId || !answeredQuestions) return null;
    for (const q of answeredQuestions) {
      for (const group of q.answer_groups) {
        for (const answer of group.answers) {
          for (const citation of answer.citations) {
            if (citation.id !== citationId) continue;
            if (!("sheet_name" in citation) || !citation.sheet_name) {
              return null;
            }
            const ref = parseCellRef(citation.cell_ref);
            return {
              sheet: citation.sheet_name,
              row: citation.row_index ?? ref?.row ?? null,
              col: citation.col_index ?? ref?.col ?? null,
              imprecise: citation.cell_imprecise ?? false,
            };
          }
        }
      }
    }
    return null;
  }, [citationId, answeredQuestions]);
};

/** Pixel geometry for one sheet: per-track sizes and cumulative offsets. */
interface SheetGeometry {
  colPx: number[]; // 1-indexed; 0 = hidden
  colOff: number[]; // colOff[c] = sum of widths of cols 1..c
  rowPx: number[];
  rowOff: number[];
  frozenRows: number;
  frozenCols: number;
  frozenHeight: number;
}

/** Per-track pixel sizes + cumulative offsets (off[i] = sum of px[1..i]). */
const buildTracks = (
  count: number,
  customSizes: Record<string, number> | undefined,
  hidden: Set<number>,
  defaultPx: number,
  toPx: (size: number) => number,
) => {
  const px = new Array<number>(count + 1).fill(0);
  const off = new Array<number>(count + 1).fill(0);
  for (let i = 1; i <= count; i++) {
    const custom = customSizes?.[String(i)];
    px[i] = hidden.has(i) ? 0 : custom != null ? toPx(custom) : defaultPx;
    off[i] = off[i - 1] + px[i];
  }
  return { px, off };
};

const buildGeometry = (
  layout: SpreadsheetSheetLayout | null | undefined,
  maxRow: number,
  maxCol: number,
): SheetGeometry => {
  const { px: colPx, off: colOff } = buildTracks(
    maxCol,
    layout?.col_widths,
    new Set(layout?.hidden_cols ?? []),
    colCharsToPx(layout?.default_col_width ?? DEFAULT_COL_CHARS),
    colCharsToPx,
  );
  const { px: rowPx, off: rowOff } = buildTracks(
    maxRow,
    layout?.row_heights,
    new Set(layout?.hidden_rows ?? []),
    ptToPx(layout?.default_row_height ?? DEFAULT_ROW_PT),
    ptToPx,
  );

  const frozenRows = Math.min(layout?.frozen_rows ?? 0, maxRow);
  return {
    colPx,
    colOff,
    rowPx,
    rowOff,
    frozenRows,
    frozenCols: Math.min(layout?.frozen_cols ?? 0, maxCol),
    frozenHeight: rowOff[frozenRows],
  };
};

interface MergeInfo {
  /** "row:col" of a merge anchor -> spanned size. */
  anchors: Map<string, { rowSpan: number; colSpan: number }>;
  /** "row:col" of a covered (non-anchor) cell -> its anchor's row. */
  covered: Map<string, number>;
}

const buildMerges = (
  layout: SpreadsheetSheetLayout | null | undefined,
  maxRow: number,
  maxCol: number,
): MergeInfo => {
  const anchors = new Map<string, { rowSpan: number; colSpan: number }>();
  const covered = new Map<string, number>();
  for (const [minR, minC, maxR, maxC] of layout?.merges ?? []) {
    const endR = Math.min(maxR, maxRow);
    const endC = Math.min(maxC, maxCol);
    if (minR > maxRow || minC > maxCol) continue;
    if ((endR - minR + 1) * (endC - minC + 1) > MAX_MERGE_AREA) continue;
    anchors.set(`${minR}:${minC}`, {
      rowSpan: endR - minR + 1,
      colSpan: endC - minC + 1,
    });
    for (let r = minR; r <= endR; r++) {
      for (let c = minC; c <= endC; c++) {
        if (r !== minR || c !== minC) covered.set(`${r}:${c}`, minR);
      }
    }
  }
  return { anchors, covered };
};

/** First row r in [lo, hi] whose bottom edge (rowOff[r]) exceeds target. */
const rowAtOffset = (
  rowOff: number[],
  target: number,
  lo: number,
  hi: number,
): number => {
  let low = lo;
  let high = hi;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (rowOff[mid] > target) high = mid;
    else low = mid + 1;
  }
  return low;
};

/**
 * Read-only spreadsheet grid for natively-parsed XLSX documents.
 *
 * Renders the workbook's captured layout: per-column widths and row heights,
 * merged cells, frozen panes, hidden rows/columns, cell styles (bold, fill,
 * font color, alignment), and Excel-style text spill across empty neighbors.
 * Sheet tabs, a formula bar, a values/formulas toggle, and citation deep-link
 * (scroll to + highlight the cited cell) round out the viewer. Rows are
 * windowed via cumulative offsets so large sheets stay cheap to render.
 */
export const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  documentId,
  target,
}) => {
  const { data: meta, isLoading: metaLoading } = useSpreadsheetMeta(documentId);
  const citedCell = useCitedCell(documentId, target?.citationId);

  const [activeSheet, setActiveSheet] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<{
    row: number;
    col: number;
  } | null>(null);
  const [showFormulas, setShowFormulas] = React.useState(false);
  const [scrollTop, setScrollTop] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const didAutoScroll = React.useRef(false);

  // Default to the cited sheet when deep-linking, else the first sheet.
  React.useEffect(() => {
    if (activeSheet || !meta?.sheets.length) return;
    const cited = citedCell?.sheet;
    const sheetNames = meta.sheets.map((s) => s.sheet_name);
    setActiveSheet(
      cited && sheetNames.includes(cited) ? cited : sheetNames[0],
    );
  }, [meta, citedCell, activeSheet]);

  const { data: sheetData, isLoading: sheetLoading } = useSpreadsheetSheet(
    documentId,
    activeSheet,
  );

  const sheetMeta = meta?.sheets.find((s) => s.sheet_name === activeSheet);
  const layout = sheetMeta?.layout;
  const maxRow = sheetMeta?.max_row ?? 0;
  const maxCol = Math.min(sheetMeta?.max_col ?? 0, MAX_RENDER_COLS);

  const geom = React.useMemo(
    () => buildGeometry(layout, maxRow, maxCol),
    [layout, maxRow, maxCol],
  );
  const merges = React.useMemo(
    () => buildMerges(layout, maxRow, maxCol),
    [layout, maxRow, maxCol],
  );

  const cellMap = React.useMemo(() => {
    const map = new Map<string, SpreadsheetCellData>();
    for (const c of sheetData?.cells ?? []) {
      map.set(`${c.row}:${c.col}`, c);
    }
    return map;
  }, [sheetData]);

  const highlight =
    citedCell && citedCell.sheet === activeSheet ? citedCell : null;

  // One-shot scroll to the cited cell once its sheet's data is up.
  React.useEffect(() => {
    if (
      didAutoScroll.current ||
      !highlight?.row ||
      !containerRef.current ||
      !sheetData
    ) {
      return;
    }
    const el = containerRef.current;
    const { rowOff, colOff, frozenHeight } = geom;
    const top = rowOff[Math.min(highlight.row, maxRow) - 1] - frozenHeight;
    el.scrollTop = Math.max(0, top - el.clientHeight / 3);
    if (highlight.col) {
      const left = GUTTER_WIDTH + colOff[Math.min(highlight.col, maxCol) - 1];
      el.scrollLeft = Math.max(0, left - el.clientWidth / 3);
    }
    didAutoScroll.current = true;
  }, [highlight, sheetData, geom, maxRow, maxCol]);

  if (metaLoading) {
    return <LoadingSpinner delay={false} />;
  }
  if (!meta?.sheets.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No spreadsheet data available for this document.
      </div>
    );
  }

  const { colPx, colOff, rowPx, rowOff, frozenRows, frozenCols, frozenHeight } =
    geom;
  const totalWidth = GUTTER_WIDTH + colOff[maxCol];
  const bodyStart = frozenRows + 1;

  // Row window over the scrollable body (frozen rows render in the sticky
  // header, so body offsets are shifted up by frozenHeight).
  const viewportHeight = containerRef.current?.clientHeight ?? 800;
  const firstRow = Math.max(
    bodyStart,
    rowAtOffset(rowOff, scrollTop + frozenHeight, bodyStart, maxRow) -
      OVERSCAN_ROWS,
  );
  const lastRow = Math.min(
    maxRow,
    rowAtOffset(
      rowOff,
      scrollTop + frozenHeight + viewportHeight,
      bodyStart,
      maxRow,
    ) + OVERSCAN_ROWS,
  );

  const selectedCell = selected
    ? cellMap.get(`${selected.row}:${selected.col}`)
    : null;
  const formulaBarText = selected
    ? (selectedCell?.formula ?? selectedCell?.value ?? "")
    : "";

  const cellText = (cell?: SpreadsheetCellData) => {
    if (!cell) return "";
    if (showFormulas && cell.formula) return cell.formula;
    return cell.value ?? "";
  };

  const renderCell = (r: number, c: number) => {
    if (colPx[c] === 0) return null; // hidden column
    const key = `${r}:${c}`;
    const coveredAnchorRow = merges.covered.get(key);
    // Covered by a merge anchor in the same row: the anchor's width absorbs
    // this cell. Anchors in earlier rows paint over via height + z-index, so
    // those still need a placeholder to keep flex offsets correct.
    if (coveredAnchorRow === r) return null;

    const cell = cellMap.get(key);
    const anchor = merges.anchors.get(key);
    let width = colPx[c];
    let height: number | undefined;
    if (anchor) {
      width = colOff[Math.min(c + anchor.colSpan - 1, maxCol)] - colOff[c - 1];
      if (anchor.rowSpan > 1) {
        height = rowOff[Math.min(r + anchor.rowSpan - 1, maxRow)] - rowOff[r - 1];
      }
    }

    const isHighlight =
      highlight?.row === r && (highlight.col === null || highlight.col === c);
    const isSelected = selected?.row === r && selected?.col === c;
    const isFrozenCol = c <= frozenCols;
    const style = cell?.style ?? undefined;
    const align =
      style?.ha ?? (cell?.data_type === "n" && !anchor ? "right" : "left");
    const background = isHighlight
      ? undefined // bg-yellow-200 class wins
      : style?.bg
        ? `#${style.bg}`
        : anchor || isFrozenCol
          ? "#ffffff"
          : undefined;

    const text = cellText(cell);
    // Excel-style spill: left-aligned text overflows across empty neighbors.
    let spill = 0;
    if (text && !anchor && align === "left" && coveredAnchorRow === undefined) {
      const spillEnd = Math.min(c + MAX_SPILL_COLS, maxCol);
      for (let ci = c + 1; ci <= spillEnd && !cellMap.has(`${r}:${ci}`); ci++) {
        spill += colPx[ci];
      }
    }

    return (
      <div
        key={c}
        onClick={() => setSelected({ row: r, col: c })}
        className={cn(
          "relative flex-shrink-0 cursor-default border-r border-gray-100 text-xs",
          spill === 0 && "truncate px-1.5 py-0.5",
          align === "right" && NUMERIC_CELL_CLASS,
          align === "center" && "text-center",
          cell?.is_header && !style?.bg && "bg-muted",
          (cell?.is_header || style?.b) && "font-medium",
          isHighlight && "bg-yellow-200",
          isSelected && "ring-2 ring-inset ring-blue-500",
          anchor && "z-[1] border-b border-gray-100",
          isFrozenCol && "sticky z-[5]",
        )}
        style={{
          width,
          height,
          backgroundColor: background,
          color: style?.fc ? `#${style.fc}` : undefined,
          left: isFrozenCol ? GUTTER_WIDTH + colOff[c - 1] : undefined,
        }}
        title={cell?.formula ?? cell?.value ?? undefined}
      >
        {spill > 0 ? (
          <span
            className="absolute inset-y-0 left-0 z-[1] flex items-center overflow-hidden whitespace-nowrap px-1.5"
            style={{ width: width + spill }}
          >
            {text}
          </span>
        ) : (
          text
        )}
      </div>
    );
  };

  const renderRow = (r: number, opts?: { inHeader?: boolean }) => {
    if (rowPx[r] === 0) return null; // hidden row
    return (
      <div
        key={r}
        className={cn(
          "flex border-b border-gray-100",
          !opts?.inHeader && "bg-white",
        )}
        style={{ height: rowPx[r] }}
      >
        <div
          className="sticky left-0 z-10 flex flex-shrink-0 items-center justify-center border-b border-r border-gray-300 bg-gray-50 text-xs text-gray-500"
          style={{ width: GUTTER_WIDTH }}
        >
          {r}
        </div>
        {cols.map((c) => renderCell(r, c))}
      </div>
    );
  };

  const cols = range(1, maxCol);
  const frozenRowNums = range(1, frozenRows);
  const bodyRows = range(firstRow, lastRow);

  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Banners */}
      {meta.values_uncomputed && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-800">
          Some formula-derived values in this workbook could not be
          recalculated and may be missing.
        </div>
      )}
      {highlight?.imprecise && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-800">
          Approximate location — the citation could not be verified to a single
          cell on this sheet.
        </div>
      )}

      {/* Formula bar */}
      <div className="flex h-9 flex-shrink-0 items-center gap-2 border-b border-gray-200 px-3">
        <span className="w-16 flex-shrink-0 rounded border border-gray-200 bg-muted px-2 py-0.5 text-center font-mono text-xs">
          {selected ? `${colToLetter(selected.col)}${selected.row}` : ""}
        </span>
        <span
          className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700"
          title={formulaBarText}
        >
          {formulaBarText}
        </span>
        <label className="flex flex-shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showFormulas}
            onChange={(e) => setShowFormulas(e.target.checked)}
          />
          Show formulas
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex flex-shrink-0 cursor-default items-center gap-1 text-xs text-muted-foreground">
                <Info className="size-3.5" />
                Read-only preview
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[320px]">
              This preview shows the workbook&apos;s last saved values and
              layout. Macros don&apos;t run, charts and some formatting
              aren&apos;t shown, and formulas aren&apos;t recalculated.
              Download the file to review it in full.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Grid */}
      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-auto"
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        <div style={{ width: totalWidth }}>
          {/* Sticky block: column letters + frozen rows */}
          <div className="sticky top-0 z-20 border-b border-gray-300 bg-white">
            <div
              className="flex border-b border-gray-300 bg-gray-100"
              style={{ height: HEADER_HEIGHT }}
            >
              <div
                className="sticky left-0 z-30 flex-shrink-0 border-r border-gray-300 bg-gray-100"
                style={{ width: GUTTER_WIDTH }}
              />
              {cols.map((c) =>
                colPx[c] === 0 ? null : (
                  <div
                    key={c}
                    className={cn(
                      "flex flex-shrink-0 items-center justify-center border-r border-gray-200 text-xs font-medium text-gray-600",
                      c <= frozenCols && "sticky z-[5] bg-gray-100",
                    )}
                    style={{
                      width: colPx[c],
                      left:
                        c <= frozenCols
                          ? GUTTER_WIDTH + colOff[c - 1]
                          : undefined,
                    }}
                  >
                    {colToLetter(c)}
                  </div>
                ),
              )}
            </div>
            {frozenRowNums.map((r) => renderRow(r, { inHeader: true }))}
          </div>

          {/* Top spacer for windowed-out rows */}
          {firstRow > bodyStart && (
            <div style={{ height: rowOff[firstRow - 1] - frozenHeight }} />
          )}

          {bodyRows.map((r) => renderRow(r))}

          {/* Bottom spacer */}
          {lastRow < maxRow && (
            <div style={{ height: rowOff[maxRow] - rowOff[lastRow] }} />
          )}
        </div>
        {sheetLoading && <LoadingSpinner delay={false} />}
      </div>

      {/* Sheet tabs */}
      <div className="flex h-9 flex-shrink-0 items-center gap-1 overflow-x-auto border-t border-gray-300 bg-gray-50 px-2">
        {meta.sheets.map((s) => (
          <button
            key={s.sheet_name}
            onClick={() => {
              setActiveSheet(s.sheet_name);
              setSelected(null);
              setScrollTop(0);
              if (containerRef.current) containerRef.current.scrollTop = 0;
            }}
            className={cn(
              "whitespace-nowrap rounded-t border border-b-0 border-gray-300 px-3 py-1 text-xs",
              s.sheet_name === activeSheet
                ? "bg-white font-medium"
                : "bg-gray-100 text-muted-foreground hover:bg-gray-200",
              s.sheet_state !== "visible" && "italic opacity-60",
            )}
            title={
              s.sheet_state !== "visible"
                ? "Hidden sheet (hidden in the original workbook)"
                : undefined
            }
          >
            {s.sheet_name}
            {s.sheet_state !== "visible" ? " (hidden)" : ""}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SpreadsheetViewer;
